import { Building2, User, GripVertical } from "lucide-react"
import { getWorkspaceIcon } from "@/lib/workspace-icons"
import { SidebarTreeRow, WsNavBrowseRow } from "@/components/layout/sidebar/SidebarTreeRow"
import { useWorkspaceExtensions } from "@/hooks/use-workspace-extensions"
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { SidebarShortcut } from "@/lib/api"
import { cn } from "@/lib/utils"
import { testIds } from "@/lib/test-ids"
import { getShortcutIcon, pinnedShortcutContextMenuPayload } from "./left-sidebar.utils"

/** First-level row under a workspace header (Basecamp, Pinned, Documents). */
export const WS_NAV_CHILD_PAD = 26
/** Nested row inside an inline group (bookmarks, pins, folder depth 0). */
export const WS_NAV_NESTED_PAD = 20
/** Per-tree-level indent step (must match depth * 10 in wsNavRowPadLeftPx). */
export const WS_NAV_DEPTH_INDENT_PX = 10
/** Matches _sidebar-nav.scss --ws-nav-leading-gap (0.3125rem @ 16px). */
export const WS_NAV_LEADING_GAP_PX = 5
/** Matches _sidebar-nav.scss --ws-nav-leading-width (0.75rem). */
export const WS_NAV_LEADING_WIDTH_PX = 12
/** Extra pad-left on focus-zone child rows so gutter control clears folder chevron. */
export const BROWSE_FOCUS_GUTTER_RESERVE_PX = 12
/** Matches .browse-level-focus-row__slot width in _sidebar-nav.scss */
export const BROWSE_FOCUS_GUTTER_SLOT_PX = 20

export function wsNavRowPadLeftPx(
  depth: number,
  drilledDownFile = false,
  inParentFocusZone = false,
  parentGutterLeftPx?: number,
  /** Ensures children indent past parent row (fixes focus-zone parent with non-zone children). */
  parentPadLeft?: number
): number {
  const base = drilledDownFile
    ? 4 + depth * WS_NAV_DEPTH_INDENT_PX
    : WS_NAV_NESTED_PAD + depth * WS_NAV_DEPTH_INDENT_PX
  let pad = base
  if (inParentFocusZone) {
    if (parentGutterLeftPx != null) {
      const minFromGutter =
        parentGutterLeftPx +
        BROWSE_FOCUS_GUTTER_SLOT_PX +
        WS_NAV_LEADING_GAP_PX +
        WS_NAV_LEADING_WIDTH_PX
      pad = Math.max(base, minFromGutter)
    } else {
      pad = base + BROWSE_FOCUS_GUTTER_RESERVE_PX
    }
  }
  if (parentPadLeft != null && parentPadLeft > 0) {
    pad = Math.max(pad, parentPadLeft + WS_NAV_DEPTH_INDENT_PX)
  }
  return pad
}

/** Left edge of chevron slot; keep in sync with .ws-nav-row__leading { left: calc(...) }. */
export function wsNavChevronLeftPx(
  depth: number,
  drilledDownFile = false,
  inParentFocusZone = false
): number {
  const padLeft = wsNavRowPadLeftPx(depth, drilledDownFile, inParentFocusZone)
  return padLeft - WS_NAV_LEADING_GAP_PX - WS_NAV_LEADING_WIDTH_PX
}

/** Collapse/expand gutter button — aligned with parent folder chevron column. */
export function browseLevelFocusGutterLeftPx(
  parentDepth: number,
  parentInParentFocusZone = false
): number {
  const chevronLeft = wsNavChevronLeftPx(parentDepth, false, parentInParentFocusZone)
  return chevronLeft + (WS_NAV_LEADING_WIDTH_PX - 2) / 2
}

/** Gutter center from the zone owner's rendered row pad (matches chevron on screen). */
export function browseLevelFocusGutterLeftFromRowPad(parentRowPadLeft: number): number {
  const chevronLeft =
    parentRowPadLeft - WS_NAV_LEADING_GAP_PX - WS_NAV_LEADING_WIDTH_PX
  return chevronLeft + (WS_NAV_LEADING_WIDTH_PX - 2) / 2
}

export { SidebarTreeRow, WsNavBrowseRow }

// ─── Workspace icon helper ───────────────────────────────────────────────────

export function WorkspaceIcon({ icon, color, name, size = 16 }: { icon: string | null; color: string | null; name: string; size?: number }) {
  const fallback = name.toLowerCase().includes('personal') ? User : Building2
  const Icon = getWorkspaceIcon(icon, fallback)
  return <Icon style={{ width: size, height: size, color: color ?? undefined }} />
}

// ─── WorkspaceExtensionItems ────────────────────────────────────────────────
// Thin wrapper that calls useWorkspaceExtensions for a single workspace,
// filtering the integration list to only show enabled extensions.

type ConfiguredIntegrations = ReturnType<typeof import('@/hooks/use-integrations').useIntegrations>['configuredIntegrations']

interface WorkspaceExtensionItemsProps {
  workspaceId: string
  integrations: ConfiguredIntegrations
  children: (filteredIntegrations: ConfiguredIntegrations) => React.ReactNode
}

export function WorkspaceExtensionItems({ workspaceId, integrations, children }: WorkspaceExtensionItemsProps) {
  const { isExtensionEnabled } = useWorkspaceExtensions(workspaceId)
  const filtered = (integrations ?? []).filter(i =>
    i.id !== 'voice-memos' && isExtensionEnabled(i.id)
  )
  return <>{children(filtered ?? [])}</>
}

// ─── Sortable shortcut item component ───────────────────────────────────────

export function SortableShortcutItem({
  shortcut,
  isActive,
  isOpen = false,
  onDocumentClick,
  onFolderClick,
  nested,
  workspaceId,
}: {
  shortcut: SidebarShortcut
  isActive: boolean
  /** Open in a tab but not focused (working-set echo). */
  isOpen?: boolean
  onDocumentClick?: (documentId: string) => void
  onFolderClick?: (path: string) => void
  /** Child rows under Pinned parent (aligned with Basecamp sub-items) */
  nested?: boolean
  workspaceId: string
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: shortcut.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const isFolderPin = !!shortcut.folder_path
  const displayIcon = shortcut.icon ? shortcut.icon : getShortcutIcon(shortcut)
  const displayLabel = shortcut.label || shortcut.document_title || shortcut.folder_path?.split('/').filter(Boolean).pop() || 'Untitled'

  const rowCls = nested
    ? 'group ws-nav-item ws-nav-row pr-2 py-1.5 text-sm select-none rounded-sm -ml-px cursor-pointer'
    : 'group ws-nav-item ws-nav-row px-3 py-1.5 text-sm select-none rounded-sm cursor-pointer'

  const openShortcut = () => {
    if (isFolderPin && shortcut.folder_path) {
      onFolderClick?.(shortcut.folder_path)
    } else {
      onDocumentClick?.(shortcut.document_id!)
    }
  }

  return (
    <div
      ref={setNodeRef}
      role="button"
      tabIndex={0}
      style={{
        ...style,
        ...(nested
          ? ({ '--ws-nav-pad-left': `${WS_NAV_NESTED_PAD}px` } as React.CSSProperties)
          : {}),
      }}
      className={cn(
        rowCls,
        isActive
          ? 'ws-nav-item--active text-sidebar-foreground'
          : isOpen
            ? 'ws-nav-item--open'
            : 'text-sidebar-foreground/70',
      )}
      data-open={isOpen && !isActive ? 'true' : undefined}
      onClick={openShortcut}
      onContextMenu={(e) => {
        e.preventDefault()
        e.stopPropagation()
        window.dispatchEvent(
          new CustomEvent('open-context-menu', {
            detail: {
              x: e.clientX,
              y: e.clientY,
              context: pinnedShortcutContextMenuPayload(shortcut, workspaceId),
            },
          }),
        )
      }}
      data-testid={testIds.leftSidebar.workspacePinnedItemRow(workspaceId, shortcut.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          openShortcut()
        }
      }}
    >
      <button
        type="button"
        className="ws-nav-row__leading ws-nav-row__leading--grip"
        aria-label="Reorder pinned item"
        {...attributes}
        {...listeners}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <GripVertical className="w-3 h-3 text-muted-foreground" />
      </button>
      <span className="ws-nav-row__icon">{displayIcon}</span>
      <span className="ws-nav-row__label">{displayLabel}</span>
    </div>
  )
}
